import { ChangeEvent, useState } from 'react';
import { Alert, Box, Button, Divider, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Helmet } from 'react-helmet-async';
import { Outlet, useNavigate } from 'react-router-dom';
import { Check as CheckIcon, Download as DownloadIcon, FileUpload as FileUploadIcon } from '@mui/icons-material';
import { useApplySyncPlan, useDetermineSyncPlan, useExportSyncPlan } from '../hooks/api/use-sync.hook';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { NodeSyncPlan } from '../components/Sync/NodeSyncPlan';
import { CollapsableSyncPlan } from '../components/Sync/CollapsableSyncPlan';
import { NodeSyncPlanApiModel } from '../../../types/models/sync/node-sync-plan.model';
import { AttributeSyncPlanApiModel } from '../../../types/models/sync/attribute-sync-plan.model';
import { AttributeSyncPlan } from '../components/Sync/AttributeSyncPlan';
import { SymbolSyncPlanApiModel } from '../../../types/models/sync/symbol-sync-plan.model';
import { SymbolSyncPlan } from '../components/Sync/SymbolSyncPlan';

export const Sync = () => {
  const navigate = useNavigate();
  const [determineSyncPlan, { syncPlan, loading: loadingSyncPlan, error: syncPlanError }] = useDetermineSyncPlan();
  const [applySyncPlan, { loading: applyingSyncPlan, error: applySyncPlanError, appliedSyncPlan }] = useApplySyncPlan();
  const [exportSyncPlan, { loading: exportingSyncPlan, error: exportSyncPlanError }] = useExportSyncPlan();

  const [modifiedNodeSyncPlan, setModifiedNodeSyncPlan] = useState<NodeSyncPlanApiModel | undefined>(
    syncPlan?.nodeSyncPlan,
  );
  const [modifiedAttributeSyncPlan, setModifiedAttributeSyncPlan] = useState<AttributeSyncPlanApiModel | undefined>(
    syncPlan?.attributeSyncPlan,
  );
  const [modifiedSymbolSyncPlan, setModifiedSymbolSyncPlan] = useState<SymbolSyncPlanApiModel | undefined>(
    syncPlan?.symbolSyncPlan,
  );

  const handleSyncFromFile = async (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) {
      return;
    }

    const { nodeSyncPlan, attributeSyncPlan, symbolSyncPlan } = await determineSyncPlan({
      file: e.target.files[0],
      fullSync: true,
    });
    setModifiedNodeSyncPlan(nodeSyncPlan);
    setModifiedAttributeSyncPlan(attributeSyncPlan);
    setModifiedSymbolSyncPlan(symbolSyncPlan);
  };

  const renderSyncPlan = () => {
    if (loadingSyncPlan || applyingSyncPlan) {
      return <LoadingIndicator />;
    } else if (syncPlanError || applySyncPlanError || exportSyncPlanError) {
      return (
        <Alert severity="error">
          {(syncPlanError || applySyncPlanError || exportSyncPlanError)?.response?.data.message}
        </Alert>
      );
    } else if (appliedSyncPlan) {
      return <Alert severity="success">Plan applied successfully</Alert>;
    } else if (!syncPlan) {
      return null;
    }

    return (
      <Box display="flex" flexDirection="column" sx={{ height: '100%' }}>
        <Box sx={{ overflow: 'auto', height: '100%', mt: '1rem' }}>
          <Divider sx={{ mt: '1rem', mb: '1rem' }} />
          {syncPlan.nodeSyncPlan && modifiedNodeSyncPlan ? (
            <CollapsableSyncPlan title="Survey Codes" plan={syncPlan.nodeSyncPlan} modifiedPlan={modifiedNodeSyncPlan}>
              <NodeSyncPlan plan={syncPlan.nodeSyncPlan} onPlanChanged={setModifiedNodeSyncPlan} />
            </CollapsableSyncPlan>
          ) : null}
          {syncPlan.attributeSyncPlan && modifiedAttributeSyncPlan ? (
            <CollapsableSyncPlan
              title="Attributes"
              plan={syncPlan.attributeSyncPlan}
              modifiedPlan={modifiedAttributeSyncPlan}
            >
              <AttributeSyncPlan plan={syncPlan.attributeSyncPlan} onPlanChanged={setModifiedAttributeSyncPlan} />
            </CollapsableSyncPlan>
          ) : null}
          {syncPlan.symbolSyncPlan && modifiedSymbolSyncPlan ? (
            <CollapsableSyncPlan title="Symbols" plan={syncPlan.symbolSyncPlan} modifiedPlan={modifiedSymbolSyncPlan}>
              <SymbolSyncPlan plan={syncPlan.symbolSyncPlan} onPlanChanged={setModifiedSymbolSyncPlan} />
            </CollapsableSyncPlan>
          ) : null}
        </Box>
        <Box display="flex">
          <Box flexGrow={1} />
          <Box display="flex" flexGrow={0.2} sx={{ gap: '1.5rem' }}>
            <LoadingButton
              fullWidth
              variant="contained"
              startIcon={<DownloadIcon />}
              loading={exportingSyncPlan}
              onClick={() =>
                exportSyncPlan({
                  nodeSyncPlan: modifiedNodeSyncPlan,
                  attributeSyncPlan: modifiedAttributeSyncPlan,
                  symbolSyncPlan: modifiedSymbolSyncPlan,
                })
              }
            >
              Export
            </LoadingButton>
            <LoadingButton
              fullWidth
              variant="contained"
              startIcon={<CheckIcon />}
              loading={applyingSyncPlan}
              onClick={() =>
                applySyncPlan({
                  nodeSyncPlan: modifiedNodeSyncPlan,
                  attributeSyncPlan: modifiedAttributeSyncPlan,
                  symbolSyncPlan: modifiedSymbolSyncPlan,
                })
              }
            >
              Apply
            </LoadingButton>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title={'Sync'} />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }}>
          <Typography variant="h5">Sync</Typography>
          <Box flexGrow={1} />
          <Button
            sx={{ mr: 2 }}
            color="primary"
            variant="contained"
            component="label"
            startIcon={<FileUploadIcon />}
            disabled={loadingSyncPlan}
          >
            Sync from file
            <input type="file" hidden onChange={handleSyncFromFile} accept=".fxl, .json" />
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<DownloadIcon />}
            disabled={loadingSyncPlan}
            onClick={() => navigate('export')}
          >
            Export sync draft
          </Button>
        </Box>

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          {renderSyncPlan()}
        </Box>
      </Box>
    </Box>
  );
};
